<template>
  <div class="about-us">
    <!-- 头部导航 -->
    <Header />

    <!-- 主要内容 -->
    <main class="main-content">
      <!-- 顶部大图 -->
      <section class="banner-section">
        <img src="@/assets/images/wenjpg.png" class="banner-image">
      </section>

      <!-- 新闻列表部分 -->
      <section class="news-section">
        <div class="container">
          <!-- 新闻类别选择 -->
          <div class="news-categories">
            <div class="category-tabs">
              <button 
                v-for="category in categories" 
                :key="category.id"
                @click="selectCategory(category.id)"
                :class="['category-tab', { active: activeCategory === category.id }]"
              >
                <span class="tab-icon">{{ category.icon }}</span>
                <span class="tab-text">{{ getCategoryDisplayName(category) }}  </span>
              </button>
            </div>
          </div>
          
          <div class="news-content">
            <!-- 加载状态 -->
            <div v-if="loading" class="loading-container">
              <div class="loading-spinner"></div>
              <p>{{ $t('news.loading') }}</p>
            </div>
            
            <!-- 新闻列表 -->
            <div v-else-if="newsList.length > 0" class="news-list">
              <div class="news-item" v-for="news in newsList" :key="news.id" @click="openNews(news)">
                <div class="news-meta">
                  <span class="news-category">{{ getCategoryName(news.category) }}</span>
                  <span class="news-date">{{ formatDate(news.publishTime) }}</span>
                </div>
                <div class="news-link">
                  {{ news.title }}
                </div>
              </div>
            </div>
            
            <!-- 空状态 -->
            <div v-else class="empty-state">
              <div class="empty-icon">📰</div>
              <p>{{ $t('news.noData') }}</p>
            </div>
            
            <!-- Element UI 分页组件 -->
            <div class="pagination-container" v-if="total > 0">
              <el-pagination
                @current-change="handleCurrentChange"
                :current-page="currentPage"
                :page-size="pageSize"
                :total="total"
                layout="prev, pager, next, jumper, total"
                background
                class="custom-pagination"
              />
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- 新闻详情弹窗 -->
    <el-dialog
      :title="selectedNews?.title"
      :visible.sync="dialogVisible"
      width="80%"
      :before-close="handleClose"
      class="news-dialog"
    >
      <div v-if="selectedNews" class="news-detail">
        <!-- 富文本类型：渲染HTML内容 -->
        <div v-if="selectedNews.type === 1 && selectedNews.content" 
             class="rich-content" 
             v-html="selectedNews.content">
        </div>
        
        <!-- 无内容状态 -->
        <div v-else class="no-content">
          <p>暂无内容</p>
        </div>
      </div>
    </el-dialog>

    <!-- 页脚 -->
    <Footer />
  </div>
</template>

<script>
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import axios from 'axios'

export default {
  name: 'News',
  components: {
    Header,
    Footer
  },
  data() {
    return {
      activeCategory: null, // 当前选中的类别
      currentPage: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      newsList: [],
      selectedNews: null,
      dialogVisible: false,
      categories: [
        { id: null, name: '全部', icon: '📰' },
        { id: 1, name: '学术成果', icon: '🎓' },
        { id: 2, name: '产业输出', icon: '🔮' },
        { id: 3, name: '企业动态', icon: '🏢' }
      ],

    }
  },
  methods: {
    // 获取新闻列表
    async fetchNews() {
      this.loading = true
      try {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize
        }
        
        // 如果选择了特定类别，添加category参数
        if (this.activeCategory !== null) {
          params.category = this.activeCategory
        }
         //https://xapi.acode.top/site/news/list
         //https://api.ipzhixin.com
        const response = await axios.get('https://api.ipzhixin.com/site/news/list', {
          params
        })
        
        if (response.data.code === 200) {
          this.newsList = response.data.rows || []
          this.total = response.data.total || 0
        } else {
          this.$message.error(response.data.msg || '获取新闻失败')
          this.newsList = []
          this.total = 0
        }
      } catch (error) {
        console.error('获取新闻失败:', error)
        this.$message.error('网络错误，请稍后重试')
        this.newsList = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    
    // 选择类别
    selectCategory(categoryId) {
      this.activeCategory = categoryId
      this.currentPage = 1 // 重置到第一页
      this.fetchNews()
    },
    
    // 分页改变
    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchNews()
    },
    
    // 获取类别名称
    getCategoryName(categoryId) {
      const category = this.categories.find(cat => cat.id === categoryId)
      return category ? this.getCategoryDisplayName(category) : this.$t('news.unknownCategory')
    },
    
    // 获取类别显示名称（多语言）
    getCategoryDisplayName(category) {
      if (!category) return this.$t('news.unknownCategory')
      
      switch (category.id) {
        case null:
          return this.$t('news.categories.all')
        case 1:
          return this.$t('news.categories.academic')
        case 2:
          return this.$t('news.categories.industrial')
        case 3:
          return this.$t('news.categories.company')
        default:
          return category.name
      }
    },
    
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    
    // 打开新闻详情
    openNews(news) {
      // 如果是URL类型，直接在新标签页打开
      if (news.type === 2 && news.url) {
        window.open(news.url, '_blank')
        return
      }
      
      // 如果是富文本类型，在弹窗中显示
      this.selectedNews = news
      this.dialogVisible = true
    },
    
    // 关闭弹窗
    handleClose() {
      this.dialogVisible = false
      this.selectedNews = null
    }
  },
  
  mounted() {
    // 页面加载时获取全部新闻
    this.selectCategory(null)
  }
}
</script>

<style scoped>
.about-us {
  font-family: 'Helvetica Neue', Arial, sans-serif;
  color: #333;
}

.main-content {
  min-height: 80vh;
}

/* 页面标题部分 */
.page-header {
  background-color: #FFF5F0;
  padding: 2rem 0;
  border-bottom: 1px solid #e9ecef;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  text-align: center;
  color: #FF631E;
}

/* 新闻列表部分 */
.news-section {
  background-color: #FFF5F0;
  padding: 0rem 0;
}

.news-header {
  margin-bottom: 2rem;
}

.news-header .section-title {
  color: #FF631E;
  font-size: 1.8rem;
  font-weight: 600;
  text-align: center;
}

/* 新闻类别选择 */
.news-categories {
  margin-bottom: 2rem;
}

.category-tabs {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.9);
  padding: 1rem 1rem 1rem 1rem;
  margin-bottom: 3rem;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
}

.category-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1.5rem;
  border: none;
  background: transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  font-weight: 500;
  color: #666;
  position: relative;
}

.category-tab:hover {
  background: rgba(255, 99, 30, 0.1);
  color: #FF631E;
  transform: translateY(-2px);
}

.category-tab.active {
  background: linear-gradient(135deg, #FF631E, #ff8c42);
  color: white;
  box-shadow: 0 4px 15px rgba(255, 99, 30, 0.3);
}

.tab-icon {
  font-size: 1.2rem;
}

.tab-text {
  font-weight: 600;
}

.news-content {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  padding: 2rem;
}

.news-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.news-item {
  padding: 0.8rem;
  border: 1px solid #f0f0f0;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #ffffff, #fafafa);
}

.news-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(255, 99, 30, 0.2);
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #666;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #FF631E;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 空状态样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #999;
}

.empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

/* Element UI 分页组件样式 */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  padding: 1rem;
}

.custom-pagination {
  background: transparent;
}

.custom-pagination .el-pagination__total {
  color: #666;
  font-weight: 500;
}

.custom-pagination .el-pager li {
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  margin: 0 2px;
  transition: all 0.3s ease;
}

.custom-pagination .el-pager li:hover {
  background: rgba(255, 99, 30, 0.1);
  border-color: #FF631E;
  color: #FF631E;
}

.custom-pagination .el-pager li.active {
  background: linear-gradient(135deg, #FF631E, #ff8c42);
  border-color: #FF631E;
  color: white;
}

.custom-pagination .btn-prev,
.custom-pagination .btn-next {
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.custom-pagination .btn-prev:hover,
.custom-pagination .btn-next:hover {
  background: #FF631E;
  border-color: #FF631E;
  color: white;
}

/* 新闻详情弹窗样式 */
.news-dialog .el-dialog__header {
  background: linear-gradient(135deg, #FF631E, #ff8c42);
  color: white;
  padding: 1.5rem 2rem;
  border-radius: 8px 8px 0 0;
}

.news-dialog .el-dialog__title {
  color: white;
  font-weight: 600;
  font-size: 1.2rem;
}

.news-dialog .el-dialog__headerbtn .el-dialog__close {
  color: white;
  font-size: 1.5rem;
}

.news-dialog .el-dialog__body {
  padding: 0;
}

.news-detail {
  min-height: 400px;
}

.iframe-container {
  width: 100%;
  height: 600px;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
}

.news-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.rich-content {
  padding: 2rem;
  line-height: 1.8;
  color: #333;
}

.rich-content h1,
.rich-content h2,
.rich-content h3 {
  color: #FF631E;
  margin: 1.5rem 0 1rem 0;
}

.rich-content p {
  margin: 1rem 0;
}

.rich-content img {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
  margin: 1rem 0;
}

.no-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 400px;
  color: #999;
  font-size: 1.1rem;
}

.news-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.8rem;
  font-size: 0.9rem;
}

.news-category {
  background: linear-gradient(135deg, #FF631E, #ff8c42);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
}

.news-date {
  color: #999;
  font-weight: 500;
}

.news-link {
  color: #333;
  text-decoration: none;
  font-size: 1.1rem;
  line-height: 1.6;
  transition: color 0.3s ease;
  display: block;
  font-weight: 500;
}

.news-link:hover {
  color: #FF631E;
}

/* 分页组件样式 */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin: 2rem 0;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.2rem;
  color: #666;
}

.pagination-btn:hover:not(:disabled) {
  background: #FF631E;
  color: white;
  border-color: #FF631E;
  transform: translateY(-2px);
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-numbers {
  display: flex;
  gap: 0.3rem;
}

.pagination-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #ddd;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  font-weight: 500;
  color: #666;
}

.pagination-number:hover {
  background: rgba(255, 99, 30, 0.1);
  border-color: #FF631E;
  color: #FF631E;
}

.pagination-number.active {
  background: linear-gradient(135deg, #FF631E, #ff8c42);
  color: white;
  border-color: #FF631E;
  box-shadow: 0 4px 15px rgba(255, 99, 30, 0.3);
}

.pagination-info {
  text-align: center;
  color: #666;
  font-size: 0.9rem;
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

/* 通用样式 */
.container {
  width: 1200px;
  margin: 0 auto;
  padding: 4rem 0;
}

.section-title {
  font-size: 2.2rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: #333;
}

/* 关于我们内容样式 */
.about-content {
  padding: 4rem 0;
}

.about-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 3rem;
  align-items: center;
}

.about-image {
  flex: 1;
  min-width: 300px;
}

.about-img {
  width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.about-text {
  flex: 1;
  min-width: 300px;
}

.about-paragraph {
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: #666;
}

/* 价值观部分样式 */
.values-section {
  background-color: #f8f9fa;
  padding: 4rem 0;
}

.values-section .section-title {
  text-align: center;
  margin-bottom: 3rem;
}

.values-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}

.value-card {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.value-card:hover {
  transform: translateY(-5px);
}

.value-icon {
  font-size: 2.5rem;
  color: #007bff;
  margin-bottom: 1.5rem;
}

.value-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #333;
}

.value-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #666;
}
/* 顶部大图部分 */
.banner-section {
  width: 100%;
  height: 33.6rem;
  overflow: hidden;
  position: relative;
}

.banner-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
/* 响应式设计 */
@media (max-width: 1200px) {
  .container {
    width: 90%;
  }
  
  .banner-section {
    height: 600px;
  }
}

@media (max-width: 992px) {
  .values-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .banner-section {
    height: 500px;
  }
  
  .news-content {
    padding: 1.5rem;
  }
  
  .news-link {
    font-size: 0.95rem;
  }
}

@media (max-width: 768px) {
  .about-wrapper {
    flex-direction: column;
  }
  
  .values-grid {
    grid-template-columns: 1fr;
  }
  
  .page-title {
    font-size: 2rem;
  }
  
  .section-title {
    font-size: 1.8rem;
  }
  
  .banner-section {
    height: 400px;
  }
  
  .news-section {
    padding: 2rem 0;
  }
  
  .news-header {
    margin-bottom: 1.5rem;
  }
  
  .container {
    padding: 2rem 0;
  }
  
  /* 新闻类别平板响应式 */
  .category-tabs {
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.8rem;
  }
  
  .category-tab {
    padding: 0.6rem 1rem;
    font-size: 0.9rem;
  }
  
  .tab-icon {
    font-size: 1rem;
  }
  
  /* 新闻列表平板响应式 */
  .news-content {
    padding: 1.5rem;
  }
  
  .news-item {
    padding: 1.2rem;
  }
  
  .news-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .news-link {
    font-size: 1rem;
  }
  
  /* 分页平板响应式 */
  .pagination {
    gap: 0.3rem;
  }
  
  .pagination-btn,
  .pagination-number {
    width: 35px;
    height: 35px;
    font-size: 0.9rem;
  }
}

@media (max-width: 576px) {
  .page-title {
    font-size: 1.8rem;
  }
  
  .section-title {
    font-size: 1.6rem;
  }
  
  .banner-section {
    height: 300px;
  }
  
  .news-content {
    padding: 1rem;
  }
  
  /* 新闻类别手机响应式 */
  .category-tabs {
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem;
  }
  
  .category-tab {
    justify-content: center;
    padding: 0.8rem;
    width: 100%;
  }
  
  .category-tab.active {
    order: -1;
  }
  
  /* 新闻列表手机响应式 */
  .news-list {
    gap: 1rem;
  }
  
  .news-item {
    padding: 1rem;
    border-radius: 6px;
  }
  
  .news-meta {
    margin-bottom: 0.6rem;
  }
  
  .news-category {
    padding: 0.2rem 0.6rem;
    font-size: 0.75rem;
  }
  
  .news-date {
    font-size: 0.8rem;
  }
  
  .news-link {
    font-size: 0.95rem;
    line-height: 1.5;
  }
  
  /* Element UI 分页手机响应式 */
  .pagination-container {
    padding: 0.5rem;
  }
  
  .custom-pagination {
    font-size: 0.8rem;
  }
  
  .custom-pagination .el-pager li {
    min-width: 28px;
    height: 28px;
    line-height: 26px;
    font-size: 0.8rem;
    margin: 0 1px;
  }
  
  .custom-pagination .btn-prev,
  .custom-pagination .btn-next {
    width: 28px;
    height: 28px;
    font-size: 0.8rem;
  }
  
  /* 加载状态手机响应式 */
  .loading-container {
    padding: 2rem 1rem;
  }
  
  .loading-spinner {
    width: 30px;
    height: 30px;
    border-width: 3px;
  }
  
  /* 空状态手机响应式 */
  .empty-state {
    padding: 2rem 1rem;
  }
  
  .empty-icon {
    font-size: 2rem;
  }
  
  /* 新闻详情弹窗手机响应式 */
  .news-dialog {
    width: 95% !important;
    margin: 0 auto;
  }
  
  .news-dialog .el-dialog__header {
    padding: 1rem;
  }
  
  .news-dialog .el-dialog__title {
    font-size: 1rem;
    line-height: 1.4;
  }
  
  .iframe-container {
    height: 400px;
  }
  
  .rich-content {
    padding: 1rem;
    font-size: 0.9rem;
  }
  
  .rich-content h1,
  .rich-content h2,
  .rich-content h3 {
    font-size: 1.1rem;
    margin: 1rem 0 0.5rem 0;
  }
  
  .no-content {
    height: 300px;
    font-size: 1rem;
  }
}

/* 平板响应式补充 */
@media (max-width: 768px) and (min-width: 577px) {
  .news-dialog {
    width: 90% !important;
  }
  
  .iframe-container {
    height: 500px;
  }
  
  .custom-pagination .el-pager li {
    min-width: 32px;
    height: 32px;
    line-height: 30px;
  }
  
  .custom-pagination .btn-prev,
  .custom-pagination .btn-next {
    width: 32px;
    height: 32px;
  }
}
</style>